import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export type ChatAuthor = 'user' | 'wundo';

const chatMessageVariants = cva('flex w-full gap-2.5', {
  variants: {
    author: {
      /* The user's own words sit on the right, as in every messenger. */
      user: 'flex-row-reverse',
      wundo: 'flex-row',
    },
  },
  defaultVariants: { author: 'wundo' },
});

const chatBubbleVariants = cva(
  'relative max-w-[80%] rounded-lg px-3.75 py-3.25 text-md leading-relaxed',
  {
    variants: {
      author: {
        user: 'rounded-tr-xs bg-primary text-white',
        wundo: 'rounded-tl-xs border border-line bg-surface text-ink-body',
      },
    },
    defaultVariants: { author: 'wundo' },
  },
);

export interface ChatMessageProps
  extends
    Omit<React.ComponentProps<'div'>, 'style'>,
    VariantProps<typeof chatMessageVariants> {
  author: ChatAuthor;
  /** Names the speaker for a screen reader, which cannot see the alignment. */
  authorLabel: string;
  /** No bubble around the turn: for content that brings its own frame, such as a channel's card. */
  bare?: boolean;
}

/** One turn of the conversation: who said it, and what they said. */
export function ChatMessage({
  author,
  authorLabel,
  bare = false,
  className,
  children,
  ...props
}: ChatMessageProps) {
  return (
    <div
      data-slot='chat-message'
      data-author={author}
      className={cn(chatMessageVariants({ author }), className)}
      {...props}
    >
      {author === 'wundo' ? (
        <span
          data-slot='chat-message-avatar'
          aria-hidden='true'
          className='mt-0.5 flex size-6.5 shrink-0 items-center justify-center rounded-sm bg-primary text-white'
        >
          <Sparkles size={13} />
        </span>
      ) : null}
      <div className={bare ? 'relative min-w-0' : chatBubbleVariants({ author })}>
        <span className='sr-only'>{authorLabel}</span>
        {children}
      </div>
    </div>
  );
}

export type ChatMessageListProps = Omit<React.ComponentProps<'div'>, 'style'>;

/**
 * The conversation so far.
 *
 * A log rather than a list: assistive technology is told new messages appear
 * at the bottom, and reads them as they arrive without stealing focus.
 */
export function ChatMessageList({ className, ...props }: ChatMessageListProps) {
  return (
    <div
      data-slot='chat-message-list'
      role='log'
      aria-live='polite'
      aria-relevant='additions'
      className={cn('flex flex-col gap-3.5', className)}
      {...props}
    />
  );
}

export { chatMessageVariants, chatBubbleVariants };
